import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { RelayLoader } from "../components/RelayLoader";
import { homeSection } from '../lib/marketingHome';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  green: "var(--pr-green)",
};

const dateLbl = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

export default function JournalPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("loading"); // loading | not_member | list
  const [reflections, setReflections] = useState([]);
  const [recapQuote, setRecapQuote] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/reflections`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/journal", { replace: true }); return; }
        if (res.status === 403) { setView("not_member"); return; }
        const data = await res.json();
        setReflections(data.reflections || []);
        setRecapQuote(data.recap_quote || null);
        setView("list");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = async () => {
    const answer = draft.trim();
    if (!answer || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/caddy/reflection`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tip_id: "journal", prompt: "", answer }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't save, try again"); return; }
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      setReflections([{ tip_id: "journal", prompt: "", answer, date: iso, created_at: new Date().toISOString() }, ...reflections]);
      setDraft("");
      toast.success("Saved to your journal 📓");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setSaving(false);
    }
  };

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    navigate("/app");
  };

  const toggleRecap = async (r) => {
    const clearing = recapQuote && recapQuote.text === r.answer;
    try {
      const res = await fetch(`${API}/api/caddy/recap-quote`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: clearing ? "" : r.answer, date: r.date }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't update, try again"); return; }
      setRecapQuote(data.recap_quote);
      toast.success(clearing ? "Removed from your recap card" : "Added, it'll shine on your next recap card ⭐");
    } catch {
      toast.error("Network hiccup, try again.");
    }
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }}>
        <RelayLoader label="Loading your journal…" />
      </div>
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="journal-join-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            The reflection journal rides with Payday Relay
          </h1>
          <Link to={homeSection('season-pass')} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-ink-surface)", color: "#fff" }}>
            See membership options →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }}>
      {/* Mobile header */}
      <header className="px-5 pt-5 pb-2 lg:hidden">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="journal-back-link">
            <ArrowLeft className="w-4 h-4" /> Payday Relay
          </Link>
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: COLOR.muted }}>📓 My Reflections</p>
        </div>
      </header>

      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="journal" onSignOut={signOut} />

        <main className="px-5 py-6 lg:px-0 lg:pt-0 pb-16">
          <div className="max-w-md mx-auto lg:max-w-5xl lg:mx-0" data-testid="journal-list">
            <h1 className="text-3xl lg:text-4xl mb-2 text-center lg:text-left" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Your reflection journal
            </h1>
            <p className="text-sm text-center lg:text-left mb-6 lg:max-w-xl leading-relaxed" style={{ color: COLOR.ink2 }} data-testid="journal-intro">
              Small thoughts, saved along the way. One line about a relay is enough. Nothing here is scored or
              shared. Star a favourite and it rides along on your weekly recap card.
            </p>

            <div className="lg:grid lg:grid-cols-[minmax(0,330px)_minmax(0,1fr)] lg:gap-8 lg:items-start">
              <div className="lg:sticky lg:top-8">
                {/* Write a reflection, anytime, not just from a coach's tip */}
                <div className="rounded-2xl p-5 lg:p-6 pr-card mb-6 lg:mb-0" data-testid="journal-composer">
              <p className="text-xs font-bold uppercase tracking-widest mb-2.5" style={{ color: COLOR.muted }}>
                ✍️ Write a reflection
              </p>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="What's on your mind about this week's race? Wins, worries, what you'd do differently…"
                className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed focus:outline-none resize-y lg:min-h-[120px]"
                style={{ borderColor: COLOR.line, color: COLOR.ink, background: "var(--pr-bg)" }}
                data-testid="journal-composer-input"
              />
              <div className="flex items-center justify-between mt-2.5">
                <p className="text-[11px]" style={{ color: COLOR.muted }}>{draft.length}/2000</p>
                <button
                  onClick={saveDraft}
                  disabled={saving || !draft.trim()}
                  className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-40 transition-transform hover:scale-[1.02]"
                  style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                  data-testid="journal-composer-save"
                >
                  {saving ? "Saving…" : "Save to journal"}
                </button>
              </div>
              </div>
            </div>

            <div className="mt-6 lg:mt-0">
              {reflections.length > 0 && (
                <p className="text-[11px] text-center lg:text-left mb-4" style={{ color: COLOR.muted }} data-testid="journal-recap-hint">
                  {reflections.length} entr{reflections.length === 1 ? "y" : "ies"} · tap ⭐ on a favorite to feature it on your shareable weekly recap card.
                </p>
              )}

              {reflections.length === 0 ? (
                <div className="text-center rounded-2xl p-5 lg:p-6 pr-card" data-testid="journal-empty">
                  <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>No reflections yet</p>
                  <p className="text-xs" style={{ color: COLOR.ink2 }}>
                    Write your first thought, or open a coach's tip in Payday Relay and jot a reply.
                  </p>
                </div>
              ) : (
                <div className="grid gap-3 items-start">
                  {reflections.map((r, i) => {
                    const featured = recapQuote && recapQuote.text === r.answer;
                    return (
                      <div
                        key={`${r.created_at}-${i}`}
                        className="rounded-2xl p-4 border"
                        style={{ background: "var(--pr-card)", borderColor: featured ? COLOR.gold : COLOR.line, borderWidth: featured ? 2 : 1 }}
                        data-testid="journal-entry"
                      >
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: COLOR.muted }}>
                          {dateLbl(r.date)}
                        </p>
                        {r.source === "quarterly_reset" && (
                          <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-1.5" style={{ border: `1px solid ${COLOR.line}`, color: COLOR.ink2 }} data-testid="journal-quarterly-tag">
                            {r.quarter?.split("-").pop()} recalibration
                          </span>
                        )}
                        {r.prompt && (
                          <p className="text-xs italic leading-relaxed mb-1.5" style={{ color: COLOR.muted }}>“{r.prompt}”</p>
                        )}
                        <p className="text-sm leading-relaxed" style={{ color: COLOR.ink }}>{r.answer}</p>
                        <button
                          onClick={() => toggleRecap(r)}
                          className="mt-2.5 text-[11px] font-bold underline"
                          style={{ color: featured ? COLOR.green : COLOR.muted }}
                          data-testid={featured ? "journal-recap-remove" : "journal-recap-add"}
                        >
                          {featured ? "⭐ On your recap card. Tap to remove" : "☆ Add to my recap card"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </div>
          </div>
        </main>
      </div>
      <div className="h-20 lg:hidden" />
      <CaddyBottomNav active="journal" />
    </div>
  );
}
